import classNames from 'classnames';
import { InvalidMappingIcon } from './InvalidMappingIcon';
import { RemoveMappingIcon } from './RemoveMappingIcon';
import { LEFT_COLUMN_EDGE } from './MappingConnectors';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import {
  getEndRowHeights,
  getFlowHoveredNode,
  getForInput,
  getLeftScrollPosition,
  getRightScrollPosition,
  getSelectedStepNodeId,
  getStepHoveredNode,
  IMapping,
  MappingPanelType,
  useMappingEditorStore,
} from '../MappingEditorStore';
import { useCallback } from 'react';
import { isAnyLineHovered, isRowHovered } from '../utils/MappingUtils';
import { getConnectorY } from './MappingConnectorUtils';

export interface IMappingConnectorOwnProps extends IMapping {
  /**
   * no connecting line, e.g. a constant mapping, or a select from flow with nothing selected
   */
  isOneSided?: boolean;
  isSelected?: boolean;
  isInvalid?: boolean;
  activeDrag?: boolean;
  validationIssuesForDataDesc?: Map<string, ValidationIssue[]>;
}
export interface IMappingConnectorProps extends IMappingConnectorOwnProps {
  rightEdge: number;
  clipPath: string;
}

// maybe these should be props from parent?
// these are a factor of how much the connector area overlaps the
// this overlap should ideally only be half the width of the widest connector point icon.
const MAPPING_CONNECTOR_CLASSNAME = 'dp-mapping-connector';

/**
 * Individual Connector line
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function MappingConnector({
  activeDrag,
  clipPath,
  dataPath,
  endIndex,
  isOneSided,
  isSelected,
  isInvalid,
  nodeName,
  startIndex,
  rightEdge,
  rightHidden,
  leftHidden,
  startId,
  endId,
}: IMappingConnectorProps) {
  const hoverNode = useMappingEditorStore(state => state.hoverNode);
  const hoverClear = useMappingEditorStore(state => state.hoverClear);
  const clearMapping = useMappingEditorStore(state => state.clearMapping);
  const leftToRight = useMappingEditorStore(getForInput);
  const leftScroll = useMappingEditorStore(getLeftScrollPosition);
  const rightScroll = useMappingEditorStore(getRightScrollPosition);
  const someLineHovered = useMappingEditorStore(
    useCallback(state => isAnyLineHovered(state.mappings, getFlowHoveredNode(state), getStepHoveredNode(state)), [])
  );
  const isHovered = useMappingEditorStore(
    useCallback(
      state => {
        return (
          isRowHovered(state.mappings, getFlowHoveredNode(state), getStepHoveredNode(state), nodeName, false) ||
          isRowHovered(state.mappings, getFlowHoveredNode(state), getStepHoveredNode(state), dataPath, true)
        );
      },
      [dataPath, nodeName]
    )
  );
  const isRowSelected = useMappingEditorStore(
    useCallback(
      state => {
        const selectedStepRowIds = getSelectedStepNodeId(state);
        return selectedStepRowIds.includes(nodeName);
      },
      [nodeName]
    )
  );

  // it's necessary to subscribe to store for this because these would otherwise re-render head of the rows themselves,
  // and will measure _before_ the row is done re-painting, so it's wrong until I hover something else, etc.
  // If this proves to be problematic we should just switch to a `MutationObserver`, instead of even relying on React for this.
  const endY = useMappingEditorStore(
    useCallback(state => getConnectorY(endIndex, rightScroll, endId, getEndRowHeights(state)), [
      endId,
      endIndex,
      rightScroll,
    ])
  );

  const startY = getConnectorY(startIndex, leftScroll, startId);

  const otherLineHovered = someLineHovered && !isHovered;

  const handleMouseEnter = () => {
    if (activeDrag) {
      return;
    }
    // hoverNode(dataPath, MappingPanelType.FLOW); // flow node hover is derived via isRowHovered for line scenario
    hoverNode(nodeName, MappingPanelType.STEP);
  };
  const onMouseEnter = () => {
    if (activeDrag) {
      return;
    }
    handleMouseEnter();
  };

  const onMouseLeave = () => {
    if (activeDrag) {
      return;
    }
    if (!isInvalid) {
      // if it's invalid, assume they might want to mouse over to converter flows
      // so don't clear hover - just let it get set by hovering over something else
      hoverClear();
    }
  };

  const onRemoveMapping = () => {
    clearMapping();
  };

  const getConnectorPoints = () => {
    const showRightCircle = !rightHidden && (!isOneSided ? true : leftToRight);
    const showLeftCircle = !leftHidden && (!isOneSided ? true : !leftToRight);
    if (isInvalid) {
      return (
        <>
          {/* left circle */}
          {showLeftCircle && <InvalidMappingIcon x={LEFT_COLUMN_EDGE} y={startY} onRemoveMapping={onRemoveMapping} />}
          {/* right circle */}
          {showRightCircle && (
            <InvalidMappingIcon
              x={rightEdge}
              y={endY}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              isHovered={isHovered}
              onRemoveMapping={onRemoveMapping}
            />
          )}
        </>
      );
    }

    return (
      <>
        {/* left circle */}
        {showLeftCircle && (
          <circle
            className={getPointClassName(isOneSided, isRowSelected, leftToRight)}
            cx={LEFT_COLUMN_EDGE}
            cy={startY}
            r={getRadius(isOneSided, isRowSelected, leftToRight)}
          />
        )}
        {/* right circle */}
        {showRightCircle && (
          <circle
            className={getPointClassName(isOneSided, isRowSelected, leftToRight)}
            cx={rightEdge}
            cy={endY}
            r={getRadius(isOneSided, isRowSelected, leftToRight)}
          />
        )}
        {!activeDrag && leftToRight && (
          <RemoveMappingIcon
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            x={rightEdge}
            y={endY}
            onClick={onRemoveMapping}
            visible
          />
        )}
      </>
    );
  };

  const lineProps = {
    x1: leftHidden ? LEFT_COLUMN_EDGE + rightEdge * 0.33 : LEFT_COLUMN_EDGE,
    y1: leftHidden ? endY : startY,
    x2: rightHidden ? rightEdge * 0.66 : rightEdge,
    y2: rightHidden ? startY : endY,
  };

  const stroke = rightHidden
    ? isInvalid
      ? 'url(#left-right-fade-invalid)'
      : 'url(#left-right-fade)'
    : leftHidden
    ? isInvalid
      ? 'url(#right-left-fade-invalid)'
      : 'url(#right-left-fade)'
    : undefined;

  return (
    <g className={getClassName(isSelected, isHovered, isInvalid, otherLineHovered)} clipPath={clipPath}>
      {!isOneSided && (
        <>
          <line
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={getHitTargetLineClassName(isHovered)}
            stroke="transparent"
            strokeWidth="12"
            strokeOpacity="0"
            {...lineProps}
          />
          <line className={getConnectorLineClassName(rightHidden, leftHidden)} {...lineProps} stroke={stroke} />
        </>
      )}
      {getConnectorPoints()}
    </g>
  );
}

function getClassName(isSelected = false, isHovered = false, isInvalid = false, otherLineHovered = false) {
  return classNames(MAPPING_CONNECTOR_CLASSNAME, {
    [`${MAPPING_CONNECTOR_CLASSNAME}--selected`]: isSelected,
    [`${MAPPING_CONNECTOR_CLASSNAME}--hovered`]: !isSelected && isHovered,
    [`${MAPPING_CONNECTOR_CLASSNAME}--neighbor-hovered`]: !isSelected && otherLineHovered,
    [`${MAPPING_CONNECTOR_CLASSNAME}--invalid`]: isInvalid,
  });
}

function getHitTargetLineClassName(isHovered = false) {
  return classNames(`${MAPPING_CONNECTOR_CLASSNAME}__hit-target-line`, {
    [`${MAPPING_CONNECTOR_CLASSNAME}__hit-target-line--hovered`]: isHovered,
  });
}

function getPointClassName(isOverlayType = false, isRowSelected = false, isForInput = true) {
  return classNames(`${MAPPING_CONNECTOR_CLASSNAME}__line-point`, {
    [`${MAPPING_CONNECTOR_CLASSNAME}__line-point-output`]: !isForInput,
    [`${MAPPING_CONNECTOR_CLASSNAME}__line-point--single`]: isOverlayType,
    [`${MAPPING_CONNECTOR_CLASSNAME}__line-point--selected`]: isOverlayType && isRowSelected,
  });
}

function getConnectorLineClassName(rightSearched = false, leftSearched = false) {
  return classNames(`${MAPPING_CONNECTOR_CLASSNAME}__line`, {
    [`${MAPPING_CONNECTOR_CLASSNAME}__line--faded`]: rightSearched || leftSearched,
  });
}

function getRadius(isOverlayType = false, isRowSelected = false, isForInput = true) {
  return (isOverlayType && isRowSelected) || !isForInput ? '5' : '4';
}
